import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import Tooltip from '@mui/material/Tooltip'
import type { ReactElement } from 'react'

import { KestrixEpcRatingChip } from '../KestrixEpcRatingChip.tsx'
import { colorTokens } from '../tokens/colors.ts'
import KestrixV2Typography from '../typography/KestrixTypography.tsx'
import type {
  CellPerformanceChipComparison,
  CellValue,
  PerformanceChipState
} from './cell-display.types.ts'
import { isCellPerformanceChip } from './cell-display.types.ts'

type ChipTone = {
  readonly surface: string
  readonly text: string
  readonly border: string
}

const CHIP_TONE_BY_STATE: Readonly<
  Record<Exclude<PerformanceChipState, 'unknown'>, ChipTone>
> = {
  outperforming: colorTokens.semantic.status.successSoftEmphasis,
  underperforming: colorTokens.semantic.status.errorSoftEmphasis,
  same: {
    surface: colorTokens.global.neutral[500],
    text: colorTokens.global.neutral[700],
    border: colorTokens.global.neutral[100]
  }
}

const MUTED_TEXT_COLOR = colorTokens.global.neutral[600]
const ICON_SIZE_PX = 14

const renderStateIcon = (
  state: Exclude<PerformanceChipState, 'unknown'>
): ReactElement => {
  if (state === 'same') {
    return (
      <KestrixV2Typography
        variant="bodySmall"
        weight="semibold"
        htmlTag="span"
        sx={{ lineHeight: 1 }}
      >
        =
      </KestrixV2Typography>
    )
  }
  const Icon = state === 'outperforming' ? ArrowUpwardIcon : ArrowDownwardIcon
  return <Icon sx={{ fontSize: ICON_SIZE_PX }} />
}

const mutedText = (text: string, testId: string): ReactElement => (
  <KestrixV2Typography
    variant="bodySmall"
    weight="regular"
    htmlTag="span"
    data-testid={testId}
    sx={{ color: MUTED_TEXT_COLOR, whiteSpace: 'nowrap' }}
  >
    {text}
  </KestrixV2Typography>
)

/** Size in px of the EPC grade chips in the comparison tooltip. */
const COMPARISON_GRADE_CHIP_SIZE_PX = 20

const COMPARISON_TOOLTIP_MAX_WIDTH_PX = 320

const renderGradeAndSap = (
  heading: string,
  grade: string | null,
  sapLabel: string | null,
  variant: 'lodged' | 'predicted'
): ReactElement => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
    <KestrixV2Typography variant="bodySmall" color="white" htmlTag="span">
      {heading}
    </KestrixV2Typography>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      <KestrixEpcRatingChip
        rating={grade ?? undefined}
        variant={variant}
        size={COMPARISON_GRADE_CHIP_SIZE_PX}
      />
      {sapLabel === null ? null : (
        <KestrixV2Typography
          variant="bodySmall"
          color="white"
          weight="medium"
          htmlTag="span"
        >
          {sapLabel}
        </KestrixV2Typography>
      )}
    </Box>
  </Box>
)

const renderComparison = (
  comparison: CellPerformanceChipComparison
): ReactElement => (
  <Box
    data-testid="performance-chip-tooltip"
    sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 0.5 }}
  >
    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.5 }}>
      {renderGradeAndSap(
        comparison.lodgedHeading,
        comparison.lodgedGrade,
        comparison.lodgedSapLabel,
        'lodged'
      )}
      <ArrowForwardIcon sx={{ fontSize: 16, mb: 0.25 }} />
      {renderGradeAndSap(
        comparison.estimatedHeading,
        comparison.estimatedGrade,
        comparison.estimatedSapLabel,
        'predicted'
      )}
    </Box>
    <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.24)' }} />
    <KestrixV2Typography variant="bodySmall" color="white" htmlTag="span">
      {comparison.sentence}
    </KestrixV2Typography>
  </Box>
)

/** A chip toned by its state, or muted text when there is nothing to compare. A comparison adds a tooltip. */
export const renderPerformanceChip = (value: CellValue): ReactElement => {
  if (!isCellPerformanceChip(value)) {
    return mutedText('—', 'performance-chip-empty')
  }
  if (value.state === 'unknown') {
    return mutedText(value.label, 'performance-chip-unknown')
  }
  const tone = CHIP_TONE_BY_STATE[value.state]
  const chip = (
    <Box
      data-testid="performance-chip"
      data-state={value.state}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        px: 1,
        py: 0.25,
        borderRadius: '100px',
        border: `1px solid ${tone.border}`,
        backgroundColor: tone.surface,
        color: tone.text,
        whiteSpace: 'nowrap'
      }}
    >
      {renderStateIcon(value.state)}
      <KestrixV2Typography
        variant="bodySmall"
        weight="medium"
        htmlTag="span"
        sx={{ color: tone.text }}
      >
        {value.label}
      </KestrixV2Typography>
    </Box>
  )
  if (value.comparison === null) {
    return chip
  }
  return (
    <Tooltip
      title={renderComparison(value.comparison)}
      placement="top"
      arrow
      // Matches the header info tooltips, and fits the sentence on one line (318px).
      slotProps={{ tooltip: { sx: { maxWidth: COMPARISON_TOOLTIP_MAX_WIDTH_PX } } }}
    >
      {chip}
    </Tooltip>
  )
}
